import { EMPTY, merge, Observable, of } from 'rxjs';
import {
  TableElementController,
  PositionedModelController,
  AbstractTableColumn,
} from '@sas-dvr/ltjs-bird';
import { ReportContextMenuState } from '@sas-dvr/internal-va-react-core/store/reportContextMenu/ReportContextMenuState';
import { ContextMenuItems } from './contextMenu';
import { ManageColumnsState } from '@sas-dvr/internal-va-react-core/store/manageColumns/manageColumnsState';
import { StateObservable } from 'redux-observable';
import { ReportState } from '@sas-dvr/internal-va-react-core/store/rootReducer';
import {
  distinctUntilChanged,
  filter,
  ignoreElements,
  map,
  share,
  switchMap,
  tap,
} from 'rxjs/operators';
import { filterNulls } from '.';
import { AnyAction } from '@sas/nova-commons/action';
import { withRetainedArray, ofActionCreatorType } from './rxUtils';

function getItems(
  visualElementController: TableElementController,
  columns: (AbstractTableColumn | null)[] | null
) {
  return filterNulls(
    columns?.map((column) => {
      const label = visualElementController.getColumnHeader(column);
      if (!label) {
        return null;
      }
      return { label, column };
    })
  );
}

export function getManageColumnsObservables(
  id: string,
  visualController: PositionedModelController | null,
  visualElementController: TableElementController,
  state$: StateObservable<ReportState>,
  actions$: Observable<AnyAction>
) {
  if (!visualController) {
    return EMPTY;
  }
  let targetId: string | undefined = undefined;

  const isContextMenuTarget$ = state$.pipe(
    map((state) => {
      const contextMenuTarget = state.ui.reportContextMenu.target?.id;
      if (contextMenuTarget) {
        targetId = contextMenuTarget;
      }
      return !!contextMenuTarget && contextMenuTarget === id;
    }),
    distinctUntilChanged(),
    share()
  );

  return isContextMenuTarget$.pipe(
    filter((isTarget) => isTarget),
    switchMap(() => {
      const columns = visualElementController.getOrderedTableColumns();
      const hiddenColumns = visualElementController.getOrderedHiddenTableColumns();
      return merge(
        of(
          ReportContextMenuState.actions.setObjectItem(ContextMenuItems.MANAGE_COLUMNS, {
            text: { key: 'ContextMenu.manageColumns.txt' },
            action: ManageColumnsState.actions.update({
              isOpen: true,
              displayedItems: getItems(visualElementController, columns),
              hiddenItems: getItems(visualElementController, hiddenColumns),
            }),
          })
        ),
        actions$.pipe(
          withRetainedArray(columns),
          withRetainedArray(hiddenColumns),
          ofActionCreatorType<typeof ManageColumnsState.actions.notifyUpdateDisplayedColumns>(
            ManageColumnsState.actionTypes.NotifyUpdateDisplayedColumns
          ),
          map((action) => action.payload),
          tap((displayedItems) => {
            if (!displayedItems || !(targetId === id)) {
              return EMPTY;
            }
            visualElementController.beginSequence();
            const displayedColumns = displayedItems.map((col) => col.column);

            const allItems = state$.value.ui.manageColumns.displayedItems?.concat(
              state$.value.ui.manageColumns.hiddenItems || []
            );
            if (allItems) {
              for (const item of allItems) {
                visualElementController.setVisibilityForColumn(
                  item.column,
                  displayedColumns.includes(item.column)
                );
              }
            }
            visualElementController.setColumnOrder(displayedColumns);
            visualElementController.endSequence();
          }),
          ignoreElements()
        )
      );
    })
  );
}
